import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@hyper-inferno/hyper-base-ng';
import { SupabaseService } from '@hyper-inferno/survey-elements-core-ui';
import { lastValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
  supabaseService = inject(SupabaseService);

  async login(email: string) {
    // Using Magic Link for "Zero-Friction" - no passwords needed

    const { error } = await this.supabaseService.client.auth.signInWithOtp({
      email,
    });

    if (error) throw error;

    alert('Check your email for the login link!');
  }

  getSession() {
    return this.supabaseService.client.auth.getSession();
  }

  private readonly baseUrl = environment.interviewer_api + '/auth';

  constructor(private http: HttpClient) {}

  async requestOtp(identifier: string) {
    return lastValueFrom(
      this.http.post(`${this.baseUrl}/request-otp`, { identifier }),
    );
  }

  async verifyOtp(identifier: string, otp: string): Promise<any> {
    // 1. Wait for Supabase to actually finish and set the session internally
    // const { data, error } = await this.supabaseService.client.auth.verifyOtp({
    //   phone: identifier,
    //   token: otp,
    //   type: 'sms',
    // });

    const { data, error } =
      await this.supabaseService.client.auth.signInWithPassword({
        // Use a ternary or logic to decide if it's an email or phone
        phone: identifier,
        password: otp,
      });

    if (error) throw error;

    // 2. Explicitly grab the token from the result instead of waiting for the Interceptor
    // This ensures the NEXT request definitely has the token
    const token = data.session?.access_token;

    return lastValueFrom(
      this.http.post(
        `${this.baseUrl}/verify-otp`,
        { identifier, otp },
        {
          headers: {
            Authorization: `Bearer ${token}`, // Pass it manually for this specific call
          },
        },
      ),
    );
  }

  async completeSignup(data: {
    identifier: string;

    userId: string;

    fName: string;

    lName: string;

    companyCode: string;
  }) {
    return lastValueFrom(this.http.post(`${this.baseUrl}/signup`, data));
  }

  async requestPersistence() {
    if (navigator.storage && navigator.storage.persist) {
      const isPersisted = await navigator.storage.persisted();

      console.log(`Storage persisted: ${isPersisted}`);

      if (!isPersisted) {
        const granted = await navigator.storage.persist();

        console.log(`Storage persistence granted: ${granted}`);

        return granted;
      }

      return isPersisted;
    }

    return false;
  }

  // A global signal for auth state
  async getUserData() {
    const {
      data: { user },
    } = await this.supabaseService.client.auth.getUser();

    return user;
  }

  async setSession(accessToken: string, refreshToken: string | undefined) {
    const { data, error } = await this.supabaseService.client.auth.setSession({
      access_token: accessToken,
      refresh_token: '',
    });

    if (error) throw error;
    return data.session;
  }
  // auth.service.ts

  async signOut() {
    const { error } = await this.supabaseService.client.auth.signOut();
    if (error) throw error;
  }
  getUserIdManual() {
    // 1. Find the Supabase key (it contains your project ID)
    const authKey = Object.keys(localStorage).find(
      (key) => key.startsWith('sb-') && key.endsWith('-auth-token'),
    );

    if (!authKey) return null;

    const rawData = localStorage.getItem(authKey);
    if (!rawData) return null;

    // 2. Parse the stored JSON
    const session = JSON.parse(rawData);

    // 3. The User ID is usually in session.user.id
    // Or you can decode the access_token JWT to find the 'sub' field
    return session.user?.id;
  }
}
